import React, { useState, useEffect } from 'react';

import mainStyle from "../../dashboard.module.scss";
import { useTheme, styled } from '@mui/material/styles';
import { Grid, Box, Typography } from '@mui/material';

import { Chart } from 'react-chartjs-2';
import { cloneDeep } from 'lodash';
import 'chart.js/auto';

import icn_bar_chart_horizontal from 'assets/images/icons/icn_bar_chart_horizontal.svg';

import { useProcessData, useProcessOption, useProcessPlugin } from '../../constProcess';

const StyledTypography = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.primary.greyPrimary,
  transition: 'color 0.3s, text-decoration-color 0.3s',
  fontSize: theme.typography.customFontSize[1],
  height: '20%',
  position: 'relative',
  width: '100%',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textAlign: 'right',

  '&:hover': {
    color: theme.palette.primary.mainHover,
    textDecorationColor: theme.palette.primary.mainHover,
    textDecoration: 'underline',
    fontWeight: theme.typography.customFontWeight[1]
  },
}));

const UsageRankingBarChart = ({ title, sortable, data, openDeviceDrawer }) => {
  const theme = useTheme();

  const defaultProcessData = useProcessData();
  const defaultProcessOption = useProcessOption();
  const defaultProcessPlugin = useProcessPlugin();

  const [chartData, setChartData] = useState(cloneDeep(defaultProcessData));
  const [chartOptions, setChartOptions] = useState(cloneDeep(defaultProcessOption));
  const [chartPlugin] = useState(cloneDeep(defaultProcessPlugin));
  const [topDevice, setTopDevice] = useState([]);

  function formattedData(data) {
    if (title === 'Top 5 PoE Power Consumption') {
      return data.map(item => ({
        percentage: Number.isFinite(item.consumption / item.budget)
          ? ((item.consumption / item.budget) * 100).toFixed(2)
          : 0,
        hover: `${(item.consumption).toFixed(2)} watt / ${(item.budget).toFixed(2)} watt`
      }));
    }
    return data.map(item => (item.percentage).toFixed(2));
  }

  useEffect(() => {
    if (data && data.length) {
      const labels = data.map(item => item.name || item.deviceId);
      const values = formattedData(data)

      let currentValues = null;
      let hoverData = null;

      if (typeof values[0] === 'object' && !Array.isArray(values[0])) {
        currentValues = values.map(item => item.percentage);
        hoverData = values.map(item => item.hover);
      }

      const tmpData = cloneDeep(defaultProcessData);

      const fillCount = Math.max(5 - data.length, 0);
      const filledLabels = [...labels];
      const filledValues = currentValues ? [...currentValues] : [...values];
      for (let i = 0; i < fillCount; i++) {
        filledLabels.push('');
        filledValues.push(null);
      }

      tmpData.labels = filledLabels;
      tmpData.datasets[0].data = filledValues;
      tmpData.datasets[0].note = hoverData ? [...hoverData] : [];
      tmpData.datasets[0].borderRadius = 4;
      tmpData.datasets[0].backgroundColor = filledLabels.map((_, index) =>
        index < data.length
          ? index % 2 === 0
            ? theme.palette.primary.main
            : theme.palette.primary.blueSecound
          : theme.palette.primary.transparent
      );

      tmpData.datasets[0].barPercentage = 0.6;
      tmpData.datasets[0].categoryPercentage = 0.8;

      const tmpOption = cloneDeep(defaultProcessOption);

      setChartData(tmpData);
      setChartOptions(tmpOption);
      setTopDevice(data);
    }
  }, [data]);

  return (
    <div
      className={`main-card-content ${mainStyle["dndObj_card"]} ${sortable ? mainStyle['dndObj_edit'] : ''}`}
      style={{ position: 'relative' }}
    >
      <div className={mainStyle['dndObj_title']}>
        <img src={icn_bar_chart_horizontal} alt={title} />
        <span style={{ marginLeft: '10px' }}>{title}</span>
      </div>
      <Box style={{ padding: '15px 0 0 0', width: 'auto', height: '165px' }}>
        {data.length > 0 ?
          <Grid container style={{ position: 'relative', width: '100%', display: 'flex' }}>
            <Box style={{ flex: '1 1', marginRight: '10px', minWidth: 'calc(70% - 20px)' }}>
              <Chart type="bar" data={chartData} options={chartOptions} plugins={[chartPlugin]} />
            </Box>
            <Box
              style={{
                whiteSpace: 'nowrap',
                flex: '0 0 10%',
                maxWidth: '30%',
                padding: '3px 0'
              }}
            >
              {topDevice.map((item, index) => (
                <StyledTypography
                  key={`${title}_${item.name || item.deviceId}_${index}`}
                  onClick={() => openDeviceDrawer('devices', true, item.deviceId)}
                >
                  {item.name || item.deviceId}
                </StyledTypography>
              ))}
            </Box>
          </Grid> :
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}>
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
              }}>
              No data available
            </Typography>
          </Box>}
      </Box>
    </div>
  );
};

export default UsageRankingBarChart;